import { formatWithValidation } from 'next/dist/shared/lib/router/utils/format-url';
import getAssetPathFromRoute from 'next/dist/shared/lib/router/utils/get-asset-path-from-route';
import { isDynamicRoute } from 'next/dist/shared/lib/router/utils/is-dynamic';
import { searchParamsToUrlQuery } from 'next/dist/shared/lib/router/utils/querystring';
import { removeTrailingSlash } from 'next/dist/shared/lib/router/utils/remove-trailing-slash';
import { getRouteMatcher } from 'next/dist/shared/lib/router/utils/route-matcher';
import { getRouteRegex } from 'next/dist/shared/lib/router/utils/route-regex';

/**
 * Intent prefetch for `getServerSideProps` data.
 *
 * The pages router never prefetches SSP data: `router.prefetch` (fired by `next/link` on
 * hover/touch) only warms JS, so every navigation still blocks on a fresh
 * `/_next/data/…json` round trip.
 *
 * On a touch/hover of an internal link we fetch the *exact* URL the router is about to
 * fetch, with the same headers. If the page answers with a cacheable response (all
 * `withCSR` pages, profile soft shell) the router's own fetch ~100ms later is served from
 * the browser HTTP cache. Nothing of Next's internal state is touched — the HTTP cache is
 * the only coupling. See docs/performance.md.
 *
 * `buildNextDataHref` mirrors Next 14.1 `router.change()` + `pageLoader.getDataHref()`;
 * its spec pins the output to URLs captured from production. Re-check on a Next upgrade.
 */

type Query = Record<string, string | string[]>;

const INTERNAL_PAGES = /^\/(_app|_error|_document|404|500|api)(\/|$)/;

export function resolvePage(cleanPathname: string, pages: string[]): string | null {
  if (pages.includes(cleanPathname)) return cleanPathname;
  // `sortedPages` is already specificity-sorted, same list the router walks.
  return pages.find(page => isDynamicRoute(page) && getRouteRegex(page).re.test(cleanPathname)) ?? null;
}

export function buildNextDataHref(params: {
  /** pathname + search, as it would appear in the address bar (`/dr/x/?a=1`) */
  asPath: string;
  pages: string[];
  buildId: string;
  locale?: string;
}): { dataHref: string; page: string } | null {
  const { asPath, pages, buildId, locale } = params;
  const url = new URL(asPath, 'http://n');
  const cleanPathname = removeTrailingSlash(url.pathname);
  const page = resolvePage(cleanPathname, pages);
  if (!page || INTERNAL_PAGES.test(page)) return null;

  // router.change(): query from the URL first, then route params assigned on top.
  const query: Query = searchParamsToUrlQuery(url.searchParams) as Query;
  if (isDynamicRoute(page)) {
    let routeMatch: ReturnType<ReturnType<typeof getRouteMatcher>>;
    try {
      routeMatch = getRouteMatcher(getRouteRegex(page))(url.pathname);
    } catch {
      return null; // malformed percent-encoding
    }
    if (!routeMatch) return null;
    Object.assign(query, routeMatch);
  }
  const { search } = new URL(formatWithValidation({ pathname: page, query }), 'http://n');

  // pageLoader.getDataHref({ skipInterpolation: true }): the *as* pathname, locale-prefixed.
  const localized = locale ? `/${locale}${cleanPathname === '/' ? '' : cleanPathname}` : cleanPathname;
  const dataRoute = getAssetPathFromRoute(removeTrailingSlash(localized), '.json');
  return { dataHref: `/_next/data/${buildId}${dataRoute}${search}`, page };
}

type NextWindow = Window & {
  __BUILD_MANIFEST?: { sortedPages?: string[] };
  __SSG_MANIFEST?: Set<string>;
  __NEXT_DATA__?: { buildId?: string };
};

type RouterLike = {
  locale?: string;
  locales?: string[];
  asPath: string;
  prefetch?: (url: string) => Promise<void>;
  events?: { on: (e: any, h: (...a: any[]) => void) => void; off: (e: any, h: (...a: any[]) => void) => void };
};

const HOVER_INTENT_MS = 80;

/**
 * Sent on every prefetch request. The server answers a prefetch it cannot make cacheable (a doctor
 * that needs a full SSR render) with a cheap `no-store` soft shell and `x-p24-prefetch-skipped`,
 * instead of running the full render — which would also send server-side profile events for a page
 * nobody opened. Request headers are not part of the HTTP cache key, so the router's own fetch
 * still reuses a cached response.
 */
export const PREFETCH_HEADER = 'x-p24-prefetch';
export const PREFETCH_SKIPPED_HEADER = 'x-p24-prefetch-skipped';

/** Doctor-profile links that are prefetched while visible (search results, recommendations). */
const VISIBLE_PREFETCH_SELECTOR = 'a[href*="/dr/"]';
/** A link must stay on screen this long (a fling past a card is not interest). */
const VISIBLE_DWELL_MS = 250;
/** Per page view: the first screens of results, not a whole long list. */
export const VISIBLE_PREFETCH_CAP = 8;
const VISIBLE_CONCURRENCY = 2;

const saveData = () => !!(navigator as any)?.connection?.saveData;

type Target = { dataHref: string; page: string; asPath: string };

/**
 * Attaches the document-level listeners. Returns a cleanup function.
 * - Intent: touch/mousedown/hover on any internal link.
 * - Visible: doctor-profile links that stay in the viewport (low priority, idle time, capped), so a
 *   tap on a search result finds the profile's `/_next/data` (an identical cacheable soft shell for
 *   non-allowlisted doctors) already in the HTTP cache and renders without a network round trip.
 * Skips: other origins, new tabs/downloads, same page, SSG pages (Next already prefetches those),
 * Save-Data, and page templates that answered `no-store` once (nothing to gain).
 */
export function startNextDataPrefetch(router: RouterLike): () => void {
  const w = window as NextWindow;
  const requested = new Set<string>();
  const uncacheablePages = new Set<string>();
  let hoverTimer: ReturnType<typeof setTimeout> | undefined;

  /** The router's data URL for an anchor, or null when there is nothing to prefetch. */
  const resolveAnchor = (anchor: HTMLAnchorElement): Target | null => {
    if (saveData()) return null;
    if (anchor.target && anchor.target !== '_self') return null;
    if (anchor.hasAttribute('download') || anchor.dataset.noDataPrefetch !== undefined) return null;
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin) return null;
    const asPath = url.pathname + url.search;
    if (asPath === router.asPath) return null;
    const firstSegment = url.pathname.split('/')[1];
    if (router.locales?.includes(firstSegment)) return null; // explicit locale switch

    const pages = w.__BUILD_MANIFEST?.sortedPages;
    const buildId = w.__NEXT_DATA__?.buildId;
    if (!pages || !buildId) return null;

    const built = buildNextDataHref({ asPath, pages, buildId, locale: router.locale });
    if (!built || requested.has(built.dataHref) || uncacheablePages.has(built.page)) return null;
    if (w.__SSG_MANIFEST?.has(built.page)) return null;
    return { ...built, asPath };
  };

  const prefetch = (target: Target, low: boolean): Promise<void> => {
    requested.add(target.dataHref);
    // The destination's JS too: next/link only prefetches links it renders with prefetch on
    // (viewport-visible, not `prefetch={false}`, not plain <a>/router.push). RUM showed
    // /center/[slug] waiting p75 ~1.2s on its chunks. router.prefetch dedupes by route.
    void router.prefetch?.(target.asPath)?.catch(() => undefined);

    // Same request shape as Next's fetchRetry(), so the HTTP cache entry is reusable.
    const init: NonNullable<Parameters<typeof fetch>[1]> & { priority?: 'low' | 'high' | 'auto' } = {
      credentials: 'same-origin',
      headers: { 'x-nextjs-data': '1', [PREFETCH_HEADER]: '1' },
    };
    if (low) init.priority = 'low';
    return fetch(target.dataHref, init)
      .then(response => {
        const cacheControl = response.headers.get('cache-control') ?? '';
        const uncacheable = /no-store/.test(cacheControl);
        // A skipped prefetch is about this one doctor, not the whole /dr/[slug] template.
        if (uncacheable && !response.headers.get(PREFETCH_SKIPPED_HEADER)) uncacheablePages.add(target.page);
        if (!response.ok || uncacheable) {
          requested.delete(target.dataHref);
          return undefined;
        }
        // Read the body so the entry is completely written to the HTTP cache.
        return response.arrayBuffer().then(() => undefined);
      })
      .catch(() => {
        requested.delete(target.dataHref);
      });
  };

  const prefetchAnchor = (anchor: HTMLAnchorElement) => {
    try {
      const target = resolveAnchor(anchor);
      if (target) void prefetch(target, false);
    } catch {
      // Prefetch is best-effort; never let it break a tap.
    }
  };

  const anchorFrom = (event: Event) =>
    (event.target instanceof Element ? event.target.closest('a[href]') : null) as HTMLAnchorElement | null;

  const onTouch = (event: Event) => {
    const anchor = anchorFrom(event);
    if (anchor) prefetchAnchor(anchor);
  };
  const onMouseOver = (event: Event) => {
    const anchor = anchorFrom(event);
    clearTimeout(hoverTimer);
    if (anchor) hoverTimer = setTimeout(() => prefetchAnchor(anchor), HOVER_INTENT_MS);
  };

  const opts = { capture: true, passive: true };
  document.addEventListener('touchstart', onTouch, opts);
  document.addEventListener('mousedown', onTouch, opts);
  document.addEventListener('mouseover', onMouseOver, opts);
  const stopVisible = startVisiblePrefetch(router, resolveAnchor, prefetch);
  return () => {
    clearTimeout(hoverTimer);
    stopVisible();
    document.removeEventListener('touchstart', onTouch, opts);
    document.removeEventListener('mousedown', onTouch, opts);
    document.removeEventListener('mouseover', onMouseOver, opts);
  };
}

/** Viewport prefetch of doctor-profile links: dwell → idle → low-priority fetch, capped per page view. */
export function startVisiblePrefetch(
  router: RouterLike,
  resolveAnchor: (a: HTMLAnchorElement) => Target | null,
  prefetch: (t: Target, low: boolean) => Promise<void>,
): () => void {
  if (typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined') return () => undefined;
  const ric = (window as any).requestIdleCallback as undefined | ((cb: () => void, o?: object) => number);
  const idle = (cb: () => void) => (ric ? ric(cb, { timeout: 1500 }) : setTimeout(cb, 200));

  let issued = 0;
  let running = 0;
  const queue: HTMLAnchorElement[] = [];
  const dwell = new Map<Element, ReturnType<typeof setTimeout>>();
  let observed = new WeakSet<Element>();

  const pump = () => {
    while (running < VISIBLE_CONCURRENCY && issued < VISIBLE_PREFETCH_CAP && queue.length) {
      const anchor = queue.shift()!;
      if (!anchor.isConnected) continue;
      let target: Target | null = null;
      try {
        target = resolveAnchor(anchor);
      } catch {
        target = null;
      }
      if (!target) continue;
      issued += 1;
      running += 1;
      void prefetch(target, true).finally(() => {
        running -= 1;
        idle(pump);
      });
    }
  };

  const io = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const anchor = entry.target as HTMLAnchorElement;
      if (entry.isIntersecting) {
        if (dwell.has(anchor)) continue;
        dwell.set(
          anchor,
          setTimeout(() => {
            dwell.delete(anchor);
            io.unobserve(anchor);
            if (issued >= VISIBLE_PREFETCH_CAP) return;
            queue.push(anchor);
            idle(pump);
          }, VISIBLE_DWELL_MS),
        );
      } else {
        clearTimeout(dwell.get(anchor));
        dwell.delete(anchor);
      }
    }
  });

  const scan = () => {
    if (issued >= VISIBLE_PREFETCH_CAP || saveData()) return;
    document.querySelectorAll<HTMLAnchorElement>(VISIBLE_PREFETCH_SELECTOR).forEach(anchor => {
      if (observed.has(anchor)) return;
      observed.add(anchor);
      io.observe(anchor);
    });
  };
  let scanQueued = false;
  const mo = new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    idle(() => {
      scanQueued = false;
      scan();
    });
  });
  mo.observe(document.body, { childList: true, subtree: true });
  idle(scan);

  // Each page view gets its own budget; links kept alive from an earlier view are re-observed.
  const onRoute = () => {
    issued = 0;
    queue.length = 0;
    dwell.forEach(t => clearTimeout(t));
    dwell.clear();
    io.disconnect();
    observed = new WeakSet<Element>();
    idle(scan);
  };
  router.events?.on('routeChangeComplete', onRoute);
  return () => {
    router.events?.off('routeChangeComplete', onRoute);
    mo.disconnect();
    io.disconnect();
    dwell.forEach(t => clearTimeout(t));
  };
}
